import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Suspense, lazy } from "react";
import AuthCallback from "@/pages/AuthCallback";
import { Toaster } from "sonner";
import { AuthProvider, useAuth, homeFor } from "@/context/AuthContext";
import Login from "@/pages/Login";

// Code-splitting : chaque écran est chargé à la demande pour accélérer l'ouverture initiale.
const Cockpit = lazy(() => import("@/pages/Cockpit"));
const Dashboard = lazy(() => import("@/pages/modules/Dashboard"));
const Dispatch = lazy(() => import("@/pages/modules/Dispatch"));
const Drivers = lazy(() => import("@/pages/modules/Drivers"));
const Clients = lazy(() => import("@/pages/modules/Clients"));
const Fleet = lazy(() => import("@/pages/modules/Fleet"));
const Invoices = lazy(() => import("@/pages/modules/Invoices"));
const Partners = lazy(() => import("@/pages/modules/Partners"));
const Accounting = lazy(() => import("@/pages/modules/Accounting"));
const Growth = lazy(() => import("@/pages/modules/Growth"));
const Automations = lazy(() => import("@/pages/modules/Automations"));
const Access = lazy(() => import("@/pages/modules/Access"));
const Devis = lazy(() => import("@/pages/modules/Devis"));
const Tarifs = lazy(() => import("@/pages/modules/Tarifs"));
const Tenders = lazy(() => import("@/pages/modules/Tenders"));
const Mining = lazy(() => import("@/pages/modules/Mining"));
const Juridique = lazy(() => import("@/pages/modules/Juridique"));
const CgvSignees = lazy(() => import("@/pages/modules/CgvSignees"));
const Studio = lazy(() => import("@/pages/modules/Studio"));
const Contenu = lazy(() => import("@/pages/modules/Contenu"));
const CommReseau = lazy(() => import("@/pages/modules/CommReseau"));
const MissionImport = lazy(() => import("@/pages/modules/MissionImport"));
const Mission = lazy(() => import("@/pages/modules/Mission"));
const Passagers = lazy(() => import("@/pages/modules/Passagers"));
const Parametres = lazy(() => import("@/pages/modules/Parametres"));
const Planning = lazy(() => import("@/pages/modules/Planning"));
const Doublons = lazy(() => import("@/pages/modules/Doublons"));
const Communications = lazy(() => import("@/pages/modules/Communications"));
const WhatsappInbox = lazy(() => import("@/pages/modules/WhatsappInbox"));
const MissionPortal = lazy(() => import("@/pages/MissionPortal"));
const PancarteView = lazy(() => import("@/pages/PancarteView"));
const SignCgv = lazy(() => import("@/pages/SignCgv"));
const AcceptDevis = lazy(() => import("@/pages/AcceptDevis"));
const ClientPortal = lazy(() => import("@/pages/ClientPortal"));
const DriverApp = lazy(() => import("@/pages/DriverApp"));
const PaymentResult = lazy(() => import("@/pages/PaymentResult"));
const SetPassword = lazy(() => import("@/pages/SetPassword"));
const TrackMission = lazy(() => import("@/pages/TrackMission"));

function AppLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505]" data-testid="app-loader">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 rounded-full border-2 border-[var(--gold-dark)] border-t-[var(--gold)] animate-spin" />
        <div className="text-[10px] tracking-[0.35em] uppercase text-[var(--gold)]">SIDI PARIS</div>
      </div>
    </div>
  );
}

function Root() {
  const { user } = useAuth();
  if (typeof window !== "undefined" && window.location.hostname.startsWith("chauffeur.")) return <Navigate to="/chauffeur" replace />;
  return <Navigate to={user ? homeFor(user.role) : "/login"} replace />;
}

function AppRouter() {
  const location = useLocation();
  const { booting } = useAuth();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  // On ne redirige jamais tant que le rôle n'a pas été revérifié via /auth/me.
  if (booting) return <AppLoader />;
  return (
      <Suspense fallback={<AppLoader />}>
        <Routes>
          <Route path="/" element={<Root />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signer/:token" element={<SignCgv />} />
          <Route path="/devis/:token" element={<AcceptDevis />} />
          <Route path="/portail" element={<ClientPortal />} />
          <Route path="/chauffeur" element={<DriverApp />} />
          <Route path="/mission/:token" element={<MissionPortal />} />
          <Route path="/pancarte/:token" element={<PancarteView />} />
          <Route path="/activation/:token" element={<SetPassword />} />
          <Route path="/suivi/:token" element={<TrackMission />} />
          <Route path="/planning" element={<Navigate to="/cockpit/planning" replace />} />
          <Route path="/payment/success" element={<PaymentResult />} />
          <Route path="/payment/cancel" element={<PaymentResult cancelled />} />
          <Route path="/cockpit" element={<Cockpit />}>
            <Route index element={<Dashboard />} />
            <Route path="dispatch" element={<Dispatch />} />
            <Route path="planning" element={<Planning />} />
            <Route path="doublons" element={<Doublons />} />
            <Route path="mission" element={<Mission />} />
            <Route path="drivers" element={<Drivers />} />
            <Route path="clients" element={<Clients />} />
            <Route path="devis" element={<Devis />} />
            <Route path="tarifs" element={<Tarifs />} />
            <Route path="appels-offres" element={<Tenders />} />
            <Route path="import" element={<Mining />} />
            <Route path="bons" element={<MissionImport />} />
            <Route path="communications" element={<Communications />} />
            <Route path="whatsapp" element={<WhatsappInbox />} />
            <Route path="fleet" element={<Fleet />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="partners" element={<Partners />} />
            <Route path="passagers" element={<Passagers />} />
            <Route path="parametres" element={<Parametres />} />
            <Route path="juridique" element={<Juridique />} />
            <Route path="cgv" element={<CgvSignees />} />
            <Route path="studio" element={<Studio />} />
            <Route path="contenu" element={<Contenu />} />
            <Route path="comm-reseau" element={<CommReseau />} />
            <Route path="accounting" element={<Accounting />} />
            <Route path="growth" element={<Growth />} />
            <Route path="automations" element={<Automations />} />
            <Route path="access" element={<Access />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster theme="dark" position="top-right" richColors />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}
